import React from 'react';
import ImageWithCaption from 'ech-components/src/components/Image/ImageWithCaption';
import Spacer from 'ech-components/src/helpers/Spacer';
import { Grid, GridItem } from 'ech-components/src/helpers';
import {
  animations,
  colors,
  mixins,
} from 'ech-components/src/core/styles/theme/default';
import { rem } from 'polished';
import styled from '@emotion/styled';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import { optionsLead } from '@components/Content';
import { ImageInterface } from 'ech-components/src/types';
import { Link } from 'ech-components/src/primitives';
import { useIntl } from 'react-intl';

const StyledIntro = styled(Grid)`
  .gatsby-image-wrapper {
    ${mixins.responsiveStyles({
      'aspect-ratio': {
        zero: '450 / 280',
        small: '735 / 460',
        medium: '975 / 540',
        large: '1190 / 450',
        wide: '1090 / 420',
        ultra: '1260 / 480',
      },
    })}
  }
`;

const StyledSocialLink = styled(Link)`
  font-size: ${rem(14)};
  line-height: ${rem(20)};
  font-weight: 500;
  text-decoration: none;
  color: ${colors.black};

  svg {
    margin-left: ${rem(6)};

    path {
      fill: ${colors.secondarySoftPurple800};
      transition: fill ${animations.fast};
      transform: scale(2.75);
      transform-origin: center;
    }
  }

  &:hover,
  &:focus {
    color: ${colors.black};
    text-decoration: none;

    span.label::after {
      content: none;
    }

    svg path {
      fill: ${colors.primarySunYellow800};
    }
  }
`;

const Intro = ({ leadText, leadImage, pageUrl }: IntroProps) => {
  const intl = useIntl();
  const shareOnText = intl.formatMessage({
    id: 'link.static.social.share',
  });

  if (!leadText && !leadImage && !pageUrl) {
    return null;
  }

  return (
    <StyledIntro columns={12}>
      {leadText?.raw ? (
        <GridItem
          size={{ zero: 12, medium: 10, large: 8 }}
          push={{ zero: 0, medium: 1, large: 2 }}
        >
          {renderRichText(
            {
              raw: leadText.raw,
              references: [],
            },
            optionsLead,
          )}
          <Spacer space="giga" />
        </GridItem>
      ) : null}

      {pageUrl ? (
        <GridItem
          size={{ zero: 12, medium: 10, large: 8 }}
          push={{ zero: 0, medium: 1, large: 2 }}
        >
          <StyledSocialLink
            linkText={shareOnText}
            link={{
              href: `https://www.linkedin.com/shareArticle?mini=true&url=${pageUrl}`,
              title: `${shareOnText} LinkedIn`,
              target: '_blank',
            }}
            iconAfter="EchIconSocialMediaLinkedin2White"
            iconSize="25"
          />
          <Spacer space="giga" />
        </GridItem>
      ) : null}

      {leadImage?.image ? (
        <GridItem size={12} push={0}>
          <ImageWithCaption
            data={{
              ...leadImage,
              imageSizes: {
                0: [450, 280],
                480: [735, 460],
                768: [975, 540],
                1024: [1190, 450],
                1280: [1090, 420],
                1440: [1260, 480],
              },
            }}
            loading={'eager'}
          />
          <Spacer space="giga" />
        </GridItem>
      ) : null}
    </StyledIntro>
  );
};

export type IntroProps = {
  leadText?: {
    raw: string;
  };
  leadImage?: ImageInterface;
  pageUrl?: string;
};

export default Intro;
